import { sharePermissionAPI } from '../../api/request'
import { apiConstants } from '../../constants/apiConstants'
import { sharePermissionConstants } from '../../constants/sharePermissionConstants'
import { apiErrorMessages } from './apiErrorMessages'
import { constructErrorMessage } from './errorMessages'

const API_METHOD = {
  post: 'POST',
  get: 'GET',
  patch: 'PATCH',
  delete: 'DELETE',
}

const safeParseJSON = (text) => {
  if (!text || typeof text !== 'string') return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

// Common error handler
const handleAPIError = (error, type) => {
  console.error(`API Error [${type}]:`, error)
  return {
    msg: apiErrorMessages.GENERAL.somethingWentWrong,
    status: 'error',
  }
}

// Map action → readable text forms
const ACTION_TEXT = {
  approve: { past: 'approved', prog: 'approving' },
  reject: { past: 'rejected', prog: 'rejecting' },
  withdraw: { past: 'cancelled', prog: 'cancelling' },
  resume: { past: 'resumed', prog: 'resuming' },
  create: { past: 'created', prog: 'creating' },
  update: { past: 'updated', prog: 'updating' },
}

const ENDPOINT = {
  approve: apiConstants.approveOrRejectRequest,
  reject: apiConstants.approveOrRejectRequest,
  withdraw: apiConstants.withdrawSharePermissionRequest,
  resume: apiConstants.resumeSharePermissionRequest,
  get: apiConstants.SharePermissionRequest,
  getAll: apiConstants.SharePermissionRequest,
}

const SuccessMsg = {
  withdraw: sharePermissionConstants.statusMsg.requestActionCancelled,
  resume: sharePermissionConstants.statusMsg.requestActionResumed,
  requestActionResumeRejected:
    sharePermissionConstants.statusMsg.requestActionResumeRejected,
  approve: sharePermissionConstants.statusMsg.requestActionGeneral.replace(
    '{operation}',
    ACTION_TEXT.approve.past,
  ),
  reject: sharePermissionConstants.statusMsg.requestActionGeneral.replace(
    '{operation}',
    ACTION_TEXT.reject.past,
  ),
  create: (approver) =>
    sharePermissionConstants.statusMsg.requestActionSuccess
      .replace('{operation}', ACTION_TEXT.create.past)
      .replace('{approver}', approver),
  update: (approver) =>
    sharePermissionConstants.statusMsg.requestActionSuccess
      .replace('{operation}', ACTION_TEXT.update.past)
      .replace('{approver}', approver),
}

const getMessage = (type, data = {}) => {
  // special case: resume when rejected
  if (type === 'resume' && data?.status === 'REJECTED')
    return SuccessMsg?.requestActionResumeRejected ?? ''

  const entry = SuccessMsg?.[type]

  // If SuccessMsg[type] is a function (e.g., create/update), call it.
  if (['create', 'update'].includes(type))
    return typeof entry === 'function'
      ? entry(data?.approver ?? '')
      : entry ?? ''

  // Otherwise it's a string
  return entry ?? ''
}

// Common response handler
const handleAPIResponse = (response, type) => {
  if ([200, 201, 204].includes(response.status)) {
    const data = safeParseJSON(response?.text || '') || {}
    const message = getMessage(type, data)
    return {
      status: 'success',
      message: message || 'Request processed successfully',
      data,
    }
  }

  return response.status === 302
    ? { msg: apiErrorMessages.GENERAL.sessionExpired, status: 'error' }
    : constructErrorMessage(response)
}

const handleSharePermissionRequestActions = async (path, params, type) => {
  const API_ACTIONS = {
    create: async () => await sharePermissionAPI(API_METHOD.post, path, params),
    approve: async () =>
      await sharePermissionAPI(API_METHOD.post, path, params),
    reject: async () => await sharePermissionAPI(API_METHOD.post, path, params),
    withdraw: async () =>
      await sharePermissionAPI(API_METHOD.post, path, params),
    update: async () =>
      await sharePermissionAPI(API_METHOD.patch, path, params),
    get: async () => await sharePermissionAPI(API_METHOD.get, path, params),
    getAll: async () => await sharePermissionAPI(API_METHOD.get, path, params),
    resume: async () => await sharePermissionAPI(API_METHOD.post, path, params),
  }

  try {
    const permissionRequestAPI = API_ACTIONS[type]
    if (!permissionRequestAPI) {
      throw new Error(`Invalid action type: ${type}`)
    }

    const permissionRequestResponse = await permissionRequestAPI()
    return handleAPIResponse(permissionRequestResponse, type)
  } catch (error) {
    return handleAPIError(error, type)
  }
}

export { handleSharePermissionRequestActions, ACTION_TEXT, ENDPOINT }
